import type { PartRetval } from '../products/getPart'
import type { CaveOptions, RackOptions } from '../RackTypes'
import { MAIN_CAVE_GROUP } from '../products/cave/utils/caveConstants'
import { HYBRID_RIG_SKUS } from '../products/rig/hybrid/hybridConstants'
import { MMS_GROUPED_SKU } from './mms-utils/mms-constants'
import { MS_GROUPED_SKU } from './ms-utils/ms-constants'
import { areArraysIdentical, hasMMSUnitHeightOrWidthChanged } from './mms-utils/change-detection-utils'

export type CameraRetargetRule = {
  shouldRetarget: (newVal: PartRetval, oldVal: PartRetval, productSku: string) => boolean
  zoomLevel: number
}

const isCave = (productSku: string) => MAIN_CAVE_GROUP.includes(productSku)
const isMassStorageProduct = (productSku: string) => MMS_GROUPED_SKU.includes(productSku) || MS_GROUPED_SKU.includes(productSku)
const isHybridRig = (productSku: string) => HYBRID_RIG_SKUS.includes(productSku)

// '7' is getDepthSelection's code for the 70" rack depth.
const DEEP_DEPTH: RackOptions['depth'] = '7'

// Every product except Cave, which has its own rule below.
function hasRackHeightChanged(newVal: PartRetval, oldVal: PartRetval, productSku: string): boolean {
  return !isCave(productSku) && newVal.config.height !== oldVal?.config.height
}

function hasCaveConfigChanged(newVal: PartRetval, oldVal: PartRetval, productSku: string): boolean {
  if (!isCave(productSku)) return false

  const caveConfig = newVal.config as Partial<CaveOptions>
  const oldCaveConfig = oldVal?.config as Partial<CaveOptions>
  const caveKeys: (keyof CaveOptions)[] = ['height', 'isLeftSideAdded', 'isRightSideAdded', 'rightPanelWidth', 'leftPanelWidth']

  return caveKeys.some(key => caveConfig[key] !== oldCaveConfig?.[key])
}

function hasMassStorageUnitChanged(newVal: PartRetval, oldVal: PartRetval, productSku: string): boolean {
  return isMassStorageProduct(productSku) && hasMMSUnitHeightOrWidthChanged(newVal, oldVal)
}

// First render is framed where the scene is built; on SPA navigation the previous value is the loading placeholder's empty config rather than undefined, so both count as no previous config.
const hasNoPreviousConfig = (oldVal: PartRetval | undefined) => !oldVal || Object.keys(oldVal.config).length === 0

// Length only - a unit's own back/material selection must not retarget, only a unit being added or removed.
function hasHybridUnitCountChanged(newVal: PartRetval, oldVal: PartRetval, productSku: string): boolean {
  if (!isHybridRig(productSku) || hasNoPreviousConfig(oldVal)) return false

  const newCfg = newVal.config as Partial<RackOptions>
  const oldCfg = oldVal.config as Partial<RackOptions>

  return (newCfg.leftUnits?.length ?? 0) !== (oldCfg.leftUnits?.length ?? 0)
    || (newCfg.rightUnits?.length ?? 0) !== (oldCfg.rightUnits?.length ?? 0)
}

// Width is Hybrid's analogue of height - a unit going 43" to 70" moves everything outboard of it, so the rack's own width and each unit's are compared positionally.
function hasHybridWidthChanged(newVal: PartRetval, oldVal: PartRetval, productSku: string): boolean {
  if (!isHybridRig(productSku) || hasNoPreviousConfig(oldVal)) return false

  const newCfg = newVal.config as Partial<RackOptions>
  const oldCfg = oldVal.config as Partial<RackOptions>

  const unitWidths = (units: RackOptions['leftUnits']): (string | undefined)[] => (units ?? []).map(unit => unit.width)

  return newCfg.width !== oldCfg.width
    || !areArraysIdentical(unitWidths(newCfg.leftUnits), unitWidths(oldCfg.leftUnits))
    || !areArraysIdentical(unitWidths(newCfg.rightUnits), unitWidths(oldCfg.rightUnits))
}

// Only the 70" step is worth a retarget - it swaps in Cave's deep crossmember and moves the whole rack out of frame, while 24"/30"/43" differ too little to justify resetting the user's zoom.
function hasHybridDepthChanged(newVal: PartRetval, oldVal: PartRetval, productSku: string): boolean {
  if (!isHybridRig(productSku) || hasNoPreviousConfig(oldVal)) return false

  const newDepth = (newVal.config as Partial<RackOptions>).depth
  const oldDepth = (oldVal.config as Partial<RackOptions>).depth

  return newDepth !== oldDepth && (newDepth === DEEP_DEPTH || oldDepth === DEEP_DEPTH)
}

// Add new product rules here — each is independently evaluated against every scene update, so more than one can fire at once.
export const CAMERA_RETARGET_RULES: CameraRetargetRule[] = [
  { shouldRetarget: hasRackHeightChanged, zoomLevel: 1.35 },
  { shouldRetarget: hasCaveConfigChanged, zoomLevel: 1.5 },
  { shouldRetarget: hasMassStorageUnitChanged, zoomLevel: 1.5 },
  { shouldRetarget: hasHybridUnitCountChanged, zoomLevel: 1.5 },
  { shouldRetarget: hasHybridWidthChanged, zoomLevel: 1.5 },
  { shouldRetarget: hasHybridDepthChanged, zoomLevel: 1.5 },
]
